/* eslint-disable indent */
/**
 * Journey Table with Tabulator - Remote Pagination
 *
 * @package WPCore
 */

import { initCustomPagination } from '../tabulator/custom-pagination.js';

/**
 * Get filter elements from container
 * @param {HTMLElement} container - Filter container element
 * @returns {Object} Object containing searchInput and statusSelect elements
 */
function getFilterElements(container) {
	return {
		searchInput: container.querySelector('input[name="search"]'),
		statusSelect: container.querySelector('select[name="status"]')
	};
}

/**
 * Build AJAX parameters for Tabulator remote pagination
 * @param {HTMLInputElement} searchInput - Search input element
 * @param {HTMLSelectElement} statusSelect - Status select element
 * @returns {Function} Function that returns filter params
 */
function buildAjaxParams(searchInput, statusSelect) {
	return () => {
		const params = {
			action: 'load_journey_data',
			nonce: wpcore.nonce,
			status: statusSelect ? statusSelect.value : 'Waiting'
		};

		// Add search param if exists
		if (searchInput && searchInput.value.trim()) {
			params.search = searchInput.value.trim();
		}

		return params;
	};
}

/**
 * Setup filter event listeners for remote pagination
 * @param {Object} table - Tabulator instance
 * @param {HTMLInputElement} searchInput - Search input element
 * @param {HTMLSelectElement} statusSelect - Status select element
 */
function setupFilterListeners(table, searchInput, statusSelect) {
	// Reload table with current filter values
	const reloadTable = () => {
		// Reset to page 1 and reload data
		table.setPage(1);
	};

	// Handle status filter change
	if (statusSelect) {
		statusSelect.addEventListener('change', reloadTable);
	}

	// Handle search input with debounce
	if (searchInput) {
		let searchTimeout;
		searchInput.addEventListener('input', () => {
			clearTimeout(searchTimeout);
			searchTimeout = setTimeout(reloadTable, 500); // 500ms debounce
		});
	}
}

export async function initJourneyTable() {
	// Only run on coach journey view page
	const tableContainer = document.getElementById('journey-table');
	if (!tableContainer) return;

	// Get filter container first to build params
	const filterContainer = document.getElementById('journey-filter');
	const { searchInput, statusSelect } = filterContainer
		? getFilterElements(filterContainer)
		: { searchInput: null, statusSelect: null };

	// Initialize Tabulator with remote pagination
	// eslint-disable-next-line no-undef
	const table = new Tabulator('#journey-table', {
		// Remote pagination config
		ajaxURL: wpcore.ajaxUrl,
		ajaxConfig: 'POST',
		ajaxContentType: {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: function (_url, _config, params) {
				// Convert params to URLSearchParams for proper POST
				return new URLSearchParams(params).toString();
			}
		},
		ajaxParams: buildAjaxParams(searchInput, statusSelect),
		// Loading indicator
		dataLoader: true,
		dataLoaderLoading: '<div class="tabulator-loading-overlay"><div class="tabulator-loader"></div></div>',
		dataLoaderError: '<div class="tabulator-loading-overlay"><div class="text-red-500">' + wpcore.direct_t({ en: 'Error loading data', fr: 'Erreur de chargement des données' }) + '</div></div>',
		// Pagination config - remote mode
		paginationMode: 'remote',
		pagination: true,
		paginationSize: 10,
		placeholder: wpcore.direct_t({ en: 'No journeys found', fr: 'Aucun parcours trouvé' }),
		layout: 'fitColumns',
		columns: [
			{
				title: wpcore.direct_t({ en: 'Name', fr: 'Nom' }),
				field: 'name',
				width: 255,
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // Rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Status', fr: 'Statut' }),
				field: 'status',
				width: 120,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
				formatter: (cell) => cell.getValue() // Rendered server-side
			},
			{
				title: wpcore.direct_t({ en: 'Create at', fr: 'Créé le' }),
				field: 'created_at',
				width: 130,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
			},
			{
				title: wpcore.direct_t({ en: 'Start weight', fr: 'Poids de départ' }),
				field: 'start_weight',
				width: 140,
				hozAlign: 'center',
				headerHozAlign: 'center',
				vertAlign: 'middle',
				headerSort: false,
			},
			{
				title: wpcore.direct_t({ en: 'Height', fr: 'Taille' }),
				field: 'height',
				width: 100,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
			},
			{
				title: wpcore.direct_t({ en: 'Target weight', fr: 'Poids cible' }),
				field: 'target_weight',
				width: 120,
				hozAlign: 'center',
				headerHozAlign: 'center',
				headerSort: false,
				vertAlign: 'middle',
			},
			{
				title: wpcore.direct_t({ en: 'Action', fr: 'Action' }),
				field: 'post_id',
				minWidth: 150,
				hozAlign: 'center',
				headerHozAlign: 'center',
				vertAlign: 'middle',
				headerSort: false,
				formatter: (cell) => cell.getValue() // Rendered server-side
			}
		]
	});

	// Initialize custom pagination (reusable module)
	await initCustomPagination(table, {
		tableElement: tableContainer,
		containerId: 'journey-pagination'
	});

	// Setup filter listeners
	setupFilterListeners(table, searchInput, statusSelect);

	table.on('rowClick', (e, row) => {
		const data = row.getData();
		if (data.detail_url) {
			window.location.href = data.detail_url;
		}
	});
}
